import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  chakra,
  Flex,
  HStack,
  PopoverBody,
  PopoverContent,
  PopoverPositioner,
  PopoverRoot,
  PopoverTrigger,
  Portal,
  Text,
} from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import {
  IncomingSparkInvitation,
  MatchesPageData,
  MatchesPageSpark,
  UserProfileWithContactInfo,
} from "@greenflagdate/shared";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { Alert } from "../../../components/Alert/Alert";
import { useNavigate } from "react-router-dom";

import { apiReq } from "../../../lib/apiReq";
import { isLoggedIn } from "../../../lib/isLoggedIn";
import { reqSettled } from "../../../lib/reqSettled";
import { useStoreData } from "../../../lib/store/store";
import { LoaderScreen } from "../../../components/LoaderScreen/LoaderScreen";
import { Icon } from "../../../components/Icon/Icon";
import { useMediaBlob } from "../../../lib/mediaHelpers";
import {
  primaryProfileImagePath,
  useMeasuredVariantPath,
} from "../../../lib/profileMedia";
import { SparkActionsDrawer } from "../../../components/SparkActionsDrawer/SparkActionsDrawer";
import { MatchProfileModal } from "../../../components/MatchProfileModal/MatchProfileModal";
import { formatEventWhen } from "../../../lib/formatEventWhen";
import { toaster } from "../../../lib/toaster";

dayjs.extend(advancedFormat);

function Avatar({
  path,
  size,
  alt,
}: {
  path?: string;
  size: string;
  alt: string;
}) {
  const [setMeasureRef, variantPath] = useMeasuredVariantPath(path);
  const { blob } = useMediaBlob(variantPath ?? "");
  const bg = blob ? `url(${URL.createObjectURL(blob)})` : undefined;
  return (
    <Box
      ref={setMeasureRef}
      aria-label={alt}
      w={size}
      h={size}
      borderRadius="full"
      bg={bg ? "bg.subtle" : "bg.brandSoft"}
      backgroundImage={bg}
      backgroundSize="cover"
      backgroundPosition="center"
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="fg.brand"
    >
      {!bg && <Icon name="user-solid" boxSize="50%" />}
    </Box>
  );
}

function formatEventDay(startsAt: string): string {
  const d = dayjs(startsAt);
  const now = dayjs();
  const diffDays = d.startOf("day").diff(now.startOf("day"), "day");
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays > 1 && diffDays < 7) return `This ${d.format("dddd")}`;
  if (diffDays >= 7 && diffDays < 14) return `Next ${d.format("dddd")}`;
  return d.format("ddd, MMM Do");
}

function SparkPill({
  spark,
  onClick,
}: {
  spark: MatchesPageSpark;
  onClick: () => void;
}) {
  const { profile, upcomingEvent } = spark;
  const photo = primaryProfileImagePath(profile);
  const nameLabel = profile.firstName
    ? `${profile.firstName}${profile.age ? `, ${profile.age}` : ""}`
    : "";

  const NameRow = nameLabel ? (
    <Text
      textStyle="bodyLight"
      color="fg.default"
      lineHeight="1.2"
      mt="1.5"
      truncate
    >
      {nameLabel}
    </Text>
  ) : null;

  if (!upcomingEvent) {
    return (
      <chakra.button
        type="button"
        onClick={onClick}
        flexShrink={0}
        display="flex"
        flexDirection="column"
        alignItems="center"
        bg="transparent"
        border="0"
        p="0"
        cursor="pointer"
        textAlign="center"
        css={{ WebkitTapHighlightColor: "transparent" }}
        _focusVisible={{ outline: "none", boxShadow: "focusRing" }}
      >
        <Avatar path={photo} size="56px" alt={profile.firstName || "match"} />
        <Box w="56px">{NameRow}</Box>
      </chakra.button>
    );
  }

  return (
    <chakra.button
      type="button"
      onClick={onClick}
      flexShrink={0}
      display="flex"
      flexDirection="column"
      alignItems="center"
      bg="transparent"
      border="0"
      p="0"
      cursor="pointer"
      textAlign="center"
      css={{ WebkitTapHighlightColor: "transparent" }}
      _focusVisible={{ outline: "none", boxShadow: "focusRing" }}
    >
      <Flex
        align="center"
        gap="2"
        h="56px"
        pr="3"
        pl="1"
        bg="bg.subtle"
        borderRadius="full"
        boxShadow="0 1px 4px rgba(0,0,0,0.03)"
        minW="0"
      >
        <Avatar path={photo} size="48px" alt={profile.firstName || "match"} />
        <Flex direction="column" minW="0" maxW="180px">
          <Text
            textStyle="body"
            fontWeight="medium"
            color="fg.default"
            lineHeight="1.2"
            truncate
          >
            {upcomingEvent.title || upcomingEvent.activity}
          </Text>
          <Text textStyle="bodyLight" color="#21242A" lineHeight="1.2" truncate>
            {formatEventDay(upcomingEvent.startsAt)}
            {", "}
            {dayjs(upcomingEvent.startsAt).format("h:mma")}
          </Text>
        </Flex>
      </Flex>
      {NameRow}
    </chakra.button>
  );
}

function ConversationRow({
  profile,
  onClick,
}: {
  profile: UserProfileWithContactInfo;
  onClick: () => void;
}) {
  const photo = primaryProfileImagePath(profile);
  const active = profile.lastActiveWithin === "day";
  return (
    <chakra.button
      type="button"
      onClick={onClick}
      display="flex"
      alignItems="center"
      gap="3"
      w="100%"
      px="5"
      py="3"
      textAlign="left"
      bg="transparent"
      border="0"
      borderBottomWidth="1px"
      borderBottomColor="border.subtle"
      cursor="pointer"
      _hover={{ bg: "bg.subtle" }}
    >
      <Box position="relative" flexShrink={0} display="inline-block">
        <Avatar path={photo} size="56px" alt={profile.firstName || "match"} />
        {active && (
          <Box
            position="absolute"
            right="1px"
            bottom="1px"
            w="13px"
            h="13px"
            borderRadius="full"
            bg="bg.brand"
            border="2.5px solid"
            borderColor="bg.canvas"
          />
        )}
      </Box>
      <Box flex="1" minW="0">
        <Flex align="baseline" justify="space-between" gap="2" mb="0.5">
          <Text fontSize="md" fontWeight="semibold" color="fg.default" truncate>
            {profile.firstName}
            {profile.age ? `, ${profile.age}` : ""}
          </Text>
        </Flex>
        <Text fontSize="sm" color="fg.muted" truncate>
          Tap to see contact details
        </Text>
      </Box>
      <Icon name="chevron-right" boxSize="14px" color="fg.muted" />
    </chakra.button>
  );
}

function InvitationRow({
  invitation,
  onAccept,
  onDismiss,
}: {
  invitation: IncomingSparkInvitation;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const { inviter, event } = invitation;
  const photo = primaryProfileImagePath(inviter);
  return (
    <Flex
      align="center"
      gap="3"
      w="100%"
      px="5"
      py="3"
      borderBottomWidth="1px"
      borderBottomColor="border.subtle"
    >
      <Box flexShrink={0}>
        <Avatar path={photo} size="56px" alt={inviter.firstName || "spark"} />
      </Box>
      <Box flex="1" minW="0">
        <Text textStyle="caption" color="fg.default" truncate>
          {inviter.firstName} invited you
        </Text>
        <Text textStyle="inputLabel" color="fg.muted" truncate>
          {event.title || event.activity} · {formatEventWhen(event.startsAt)}
        </Text>
      </Box>
      <Flex gap="2" flexShrink={0}>
        <Button size="sm" variant="outline" onClick={onDismiss}>
          Dismiss
        </Button>
        <Button size="sm" onClick={onAccept}>
          View
        </Button>
      </Flex>
    </Flex>
  );
}

function SectionLabel({
  children,
  info,
}: {
  children: React.ReactNode;
  /** Optional tooltip copy shown from a clickable info (ⓘ) affordance. */
  info?: React.ReactNode;
}) {
  return (
    <Box px="5" pt="5" pb="3">
      <Flex align="center" gap="1.5">
        <Text
          fontSize="lg"
          fontWeight="semibold"
          color="fg.default"
          letterSpacing="-0.01em"
        >
          {children}
        </Text>
        {info && (
          <PopoverRoot positioning={{ placement: "bottom" }}>
            <PopoverTrigger asChild>
              <chakra.button
                type="button"
                aria-label="What are Mutual Sparks?"
                appearance="none"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="18px"
                h="18px"
                borderRadius="full"
                borderWidth="1.5px"
                borderColor="fg.muted"
                color="fg.muted"
                bg="transparent"
                cursor="pointer"
                fontSize="11px"
                fontWeight="bold"
                lineHeight="1"
                fontStyle="italic"
                fontFamily="serif"
                _hover={{ borderColor: "fg.brand", color: "fg.brand" }}
              >
                i
              </chakra.button>
            </PopoverTrigger>
            <Portal>
              <PopoverPositioner>
                <PopoverContent maxW="18rem" bg="bg.canvas" color="fg.default">
                  <PopoverBody>
                    <Text as="div" textStyle="inputLabel" color="fg.muted">
                      {info}
                    </Text>
                  </PopoverBody>
                </PopoverContent>
              </PopoverPositioner>
            </Portal>
          </PopoverRoot>
        )}
      </Flex>
    </Box>
  );
}

function EmptyState({ onBrowse }: { onBrowse: () => void }) {
  const steps = [
    {
      n: "01",
      t: "RSVP to an event",
      s: "Pick one that sounds fun — bring a friend.",
    },
    {
      n: "02",
      t: "Show up and say hi",
      s: "No swiping. Just meet people in real life.",
    },
    {
      n: "03",
      t: "Match on Green Flag after",
      s: "You'll have a week to match up.",
    },
  ];
  return (
    <Flex
      flex="1"
      direction="column"
      align="center"
      justify="center"
      textAlign="center"
      px="8"
      pt="6"
      pb="12"
    >
      <Box position="relative" w="180px" h="140px" mb="7">
        <Box
          position="absolute"
          left="50%"
          top="50%"
          w="180px"
          h="140px"
          transform="translate(-50%, -50%)"
          borderRadius="full"
          bg="bg.brandSoft"
          opacity={0.45}
          filter="blur(14px)"
        />
        <Box
          position="absolute"
          left="4px"
          top="28px"
          w="58px"
          h="58px"
          borderRadius="full"
          border="2px dashed"
          borderColor="fg.brandSoft"
          bg="bg.canvas"
        />
        <Box
          position="absolute"
          right="4px"
          top="28px"
          w="58px"
          h="58px"
          borderRadius="full"
          border="2px dashed"
          borderColor="fg.brandSoft"
          bg="bg.canvas"
        />
        <Flex
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(-50%, -50%)"
          w="84px"
          h="84px"
          borderRadius="full"
          bg="bg.brand"
          color="fg.onBrand"
          align="center"
          justify="center"
          boxShadow="0 0 12.1px 2px rgba(0,0,0,0.25)"
        >
          <Icon name="logo" boxSize="44px" />
        </Flex>
        <Flex
          position="absolute"
          left="48px"
          top="6px"
          w="22px"
          h="22px"
          borderRadius="full"
          bg="bg.canvas"
          color="fg.brand"
          align="center"
          justify="center"
          boxShadow="0 0 12.1px 2px rgba(0,0,0,0.05)"
        >
          <Icon name="heart-filled" boxSize="10px" />
        </Flex>
        <Flex
          position="absolute"
          right="42px"
          bottom="2px"
          w="18px"
          h="18px"
          borderRadius="full"
          bg="bg.brandTint"
          color="fg.onTint"
          align="center"
          justify="center"
        >
          <Icon name="heart-filled" boxSize="8px" />
        </Flex>
      </Box>

      <Text
        as="h2"
        fontSize="xl"
        fontWeight="semibold"
        color="fg.default"
        letterSpacing="-0.01em"
        mb="2"
      >
        No matches yet
      </Text>
      <Text fontSize="sm" color="fg.muted" lineHeight="1.5" maxW="280px" mb="7">
        Sparks happen in person. RSVP to an event this week and we'll light up
        your matches here afterward.
      </Text>

      <Button
        onClick={onBrowse}
        h="48px"
        px="7"
        borderRadius="full"
        bg="bg.brand"
        color="fg.onBrand"
        fontWeight="semibold"
        fontSize="sm"
        boxShadow="0 0 12.1px 2px rgba(0,0,0,0.05)"
      >
        <Icon name="logo" boxSize="14px" />
        Browse Events
      </Button>

      <Flex direction="column" gap="2.5" mt="9" w="100%">
        {steps.map((step) => (
          <Flex
            key={step.n}
            align="center"
            gap="3"
            px="3.5"
            py="3"
            bg="bg.subtle"
            borderRadius="xl"
            textAlign="left"
          >
            <Flex
              w="32px"
              h="32px"
              borderRadius="full"
              bg="bg.brandTint"
              color="fg.onTint"
              align="center"
              justify="center"
              fontSize="xs"
              fontWeight="bold"
              flexShrink={0}
            >
              {step.n}
            </Flex>
            <Box minW="0">
              <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                {step.t}
              </Text>
              <Text fontSize="xs" color="fg.muted" lineHeight="1.4">
                {step.s}
              </Text>
            </Box>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

export const Matches = () => {
  const storeData = useStoreData();
  const navigate = useNavigate();
  const [matchesReq, matchesReqState] = useReq<MatchesPageData>(apiReq);
  const [respondReq] = useReq(apiReq);

  const [actionsSpark, setActionsSpark] = useState<MatchesPageSpark | null>(
    null,
  );
  const [profileSpark, setProfileSpark] = useState<MatchesPageSpark | null>(
    null,
  );

  useEffect(() => {
    if (isLoggedIn(storeData)) {
      matchesReq.get("/user/matches-page");
    }
  }, [matchesReq, storeData]);

  // useReq nulls `result` synchronously when a new request starts, so a bare
  // `matchesReqState.result` would blank the whole page to the loader during
  // refetches (e.g. after dismissing an invitation). Keep the last result so
  // refetches keep the current content; it's replaced when fresh data lands.
  const lastDataRef = useRef<MatchesPageData | null>(null);
  if (matchesReqState.result) lastDataRef.current = matchesReqState.result;
  const data = matchesReqState.result ?? lastDataRef.current;
  const invitations = data?.invitations ?? [];
  const hasAnyContent = useMemo(
    () =>
      !!data &&
      (data.sparks.length > 0 ||
        data.conversations.length > 0 ||
        invitations.length > 0),
    [data, invitations.length],
  );

  const dismissInvitation = async (invitationId: string) => {
    const response = await respondReq.post(
      `/user/spark-invitations/${invitationId}/decline`,
      {},
    );
    // Refresh regardless of outcome. On success the row is gone; on
    // INVITATION_NOT_FOUND (the invite was already expired / accepted /
    // declined elsewhere, e.g. in another tab) the refetch drops the now-stale
    // row so the button isn't a silent dead end. Only a genuinely unexpected
    // failure (network / 500) warrants a toast.
    if (!response.success && response.error?.code !== "INVITATION_NOT_FOUND") {
      toaster.create({
        type: "error",
        title: "Couldn't dismiss the invitation. Please try again.",
      });
    }
    matchesReq.get("/user/matches-page");
  };

  return (
    <Flex direction="column" w="100%" maxW="40rem" mx="auto" flex="1">
      {!data && !reqSettled(matchesReqState) ? (
        // Loader until the first request settles (see reqSettled for why
        // `loading` alone would flash the empty state first). After that,
        // `data` is the cached last result, so refetches keep the current
        // content instead of flashing back to the spinner.
        <LoaderScreen />
      ) : matchesReqState.error ? (
        <Box px="5" py="4">
          <Alert intent="danger" title={matchesReqState.error.message} />
        </Box>
      ) : !hasAnyContent ? (
        <EmptyState onBrowse={() => navigate("/events")} />
      ) : (
        <>
          {invitations.length > 0 && (
            <>
              <SectionLabel>Invitations</SectionLabel>
              <Box data-testid="invitations-list">
                {invitations.map((invitation) => (
                  <InvitationRow
                    key={invitation.invitationId}
                    invitation={invitation}
                    onAccept={() =>
                      navigate(`/event/atlanta/${invitation.event.sparkId}`, {
                        state: { animate: true },
                      })
                    }
                    onDismiss={() =>
                      dismissInvitation(invitation.invitationId)
                    }
                  />
                ))}
              </Box>
            </>
          )}

          {data!.sparks.length > 0 && (
            <>
              <SectionLabel
                info="Mutual Sparks are people you've liked who liked you back — but you haven't met yet. Invite them to an event to see if it's a match!"
              >
                Mutual Sparks
              </SectionLabel>
              <HStack
                data-testid="your-sparks-list"
                gap="2.5"
                overflowX="auto"
                px="5"
                pb="1.5"
                align="stretch"
                css={{
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {data!.sparks.map((s) => (
                  <SparkPill
                    key={s.profile.id}
                    spark={s}
                    onClick={() => setActionsSpark(s)}
                  />
                ))}
                <Box w="1" flexShrink={0} />
              </HStack>
            </>
          )}

          {data!.conversations.length > 0 && (
            <>
              <SectionLabel>Matches</SectionLabel>
              <Box data-testid="matches-conversations-list">
                {data!.conversations.map((p) => (
                  <ConversationRow
                    key={p.id}
                    profile={p}
                    onClick={() => navigate(`/matches/${p.id}`)}
                  />
                ))}
              </Box>
            </>
          )}
          <Box h="4" />
        </>
      )}

      <SparkActionsDrawer
        spark={actionsSpark}
        onClose={() => setActionsSpark(null)}
        onViewEvent={(s) => {
          setActionsSpark(null);
          if (s.upcomingEvent) {
            navigate(`/event/atlanta/${s.upcomingEvent.sparkId}`, {
              state: { animate: true },
            });
          }
        }}
        onViewProfile={(s) => {
          setActionsSpark(null);
          setProfileSpark(s);
        }}
        onRegisterForEvent={(sparkId) => {
          // Carry the invite intent to the event page: once the viewer
          // registers there, the invite is sent automatically (see EventV2).
          const invitee = actionsSpark;
          setActionsSpark(null);
          navigate(`/event/atlanta/${sparkId}`, {
            state: {
              animate: true,
              inviteSparkId: invitee?.profile.id,
              inviteSparkName: invitee?.profile.firstName,
            },
          });
        }}
        onJoinSparkEvent={(sparkId) => {
          // The invitee is already going — route to their event to register
          // and JOIN them, with NO invite intent (an invite would just fail
          // ALREADY_REGISTERED).
          setActionsSpark(null);
          navigate(`/event/atlanta/${sparkId}`, {
            state: { animate: true },
          });
        }}
        onInvited={() => {
          // Refresh so the spark now carries its pending-invite status the
          // next time its drawer opens.
          matchesReq.get("/user/matches-page");
        }}
      />
      <MatchProfileModal
        profile={profileSpark?.profile ?? null}
        onClose={() => setProfileSpark(null)}
      />
    </Flex>
  );
};
